<template>
  <section class="book-content">
    <div v-if="!state.isLoaded" class="loading-state">
      {{ $t("common.loading") }}
    </div>

    <template v-else>
      <div class="content-tabs" role="tablist" :aria-label="tabAriaLabel">
        <button
          type="button"
          class="tab-button"
          :class="{ active: activeTab === 'introduction' }"
          role="tab"
          :aria-selected="activeTab === 'introduction'"
          @click="activeTab = 'introduction'"
        >
          <span class="tab-index">01</span>
          <span>{{ $t("bookContent.introTab") }}</span>
        </button>

        <button
          type="button"
          class="tab-button"
          :class="{ active: activeTab === 'resources' }"
          role="tab"
          :aria-selected="activeTab === 'resources'"
          @click="activeTab = 'resources'"
        >
          <span class="tab-index">02</span>
          <span>{{ $t("bookContent.materialsTab") }}</span>
        </button>
      </div>

      <div class="tab-panel" role="tabpanel">
        <Transition name="tab-fade" mode="out-in">
          <div
            v-if="activeTab === 'introduction'"
            key="introduction"
            class="introduction-panel"
          >
            <!-- ① Features：三張特色卡 -->
            <section v-if="featureSections.length" class="feature-card-section">
              <div class="section-heading">
                <span class="feature-card-eyebrow"> FEATURES </span>

                <h2>書籍特色</h2>
              </div>

              <div class="feature-card-list">
                <article
                  v-for="(section, index) in featureSections"
                  :key="`${section.title}-${index}`"
                  class="feature-text-card"
                >
                  <span class="feature-text-number">
                    {{ String(section.number || index + 1).padStart(2, "0") }}
                  </span>

                  <div class="feature-text-copy">
                    <h3 v-if="section.title">
                      {{ section.title }}
                    </h3>

                    <div v-if="section.content" class="feature-text-content">
                      <p
                        v-for="(
                          paragraph, paragraphIndex
                        ) in section.paragraphs"
                        :key="paragraphIndex"
                      >
                        {{ paragraph }}
                      </p>
                    </div>
                  </div>
                </article>
              </div>
            </section>

            <div class="section-heading">
              <span class="feature-card-eyebrow"> CURRICULUM </span>

              <h2>課程架構</h2>
            </div>
            <!-- ② 教材理念文章 -->
            <ArticleSection
              v-if="displayArticle"
              class="article-section"
              :content="displayArticle"
              :images="[]"
              :image-alt="bookImageAlt"
            />

            <!-- ③ 表格 -->
            <CurriculumTable
              v-if="displayTable"
              class="curriculum-section"
              :data="displayTable"
            />
            <!-- :default-title="tableDefaultTitle" -->

            <!-- ④ 圖文介紹：有目前語言的 content 時使用新版圖文版型 -->
            <IntroductionSection
              v-if="displayContent"
              class="visual-content-section"
              :content="displayContent"
              :images="[]"
              :image-alt="bookImageAlt"
              :empty-text="introEmptyText"
            />

            <!-- ④ 舊版介紹：目前語言沒有 content 時，img 可放單張或多張圖片 -->
            <div
              v-else-if="contentImages.length"
              class="legacy-content-section"
            >
              <button
                v-for="(image, imageIndex) in contentImages"
                :key="`${image}-${imageIndex}`"
                type="button"
                class="legacy-content-image-button"
                :aria-label="`${bookImageAlt} ${imageIndex + 1}－點擊放大圖片`"
                @click="openLightbox(image, `${bookImageAlt} ${imageIndex + 1}`)"
              >
                <img
                  :src="image"
                  :alt="`${bookImageAlt} ${imageIndex + 1}`"
                  class="legacy-content-image"
                  loading="lazy"
                />
                <span class="legacy-content-image-hint" aria-hidden="true">
                  點擊放大
                </span>
              </button>
            </div>

            <div
              v-if="
                !featureSections.length &&
                !displayTable &&
                !displayContent &&
                !contentImages.length
              "
              class="empty-state"
            >
              {{ introEmptyText }}
            </div>
          </div>

          <ResourceSection
            v-else
            key="resources"
            :provide="displayProvide"
            :images="provideImages"
            :image-alt="bookImageAlt"
            :links="resourceLinks"
            :materials-title="$t('bookContent.materialsTab')"
            :downloads-title="resourceTitle"
            :empty-text="resourceEmptyText"
          />
        </Transition>
      </div>
    </template>

    <Teleport to="body">
      <Transition name="lightbox-fade">
        <div
          v-if="lightboxImage"
          class="image-lightbox"
          role="dialog"
          aria-modal="true"
          :aria-label="lightboxAlt"
          @click.self="closeLightbox"
        >
      <div class="image-lightbox-panel">
          <button
            type="button"
            class="lightbox-close"
            aria-label="關閉圖片"
            @click="closeLightbox"
          >
            ×
          </button>

          <img :src="lightboxImage" :alt="lightboxAlt" class="image-lightbox-content" />
        
      </div>
    </div>
      </Transition>
    </Teleport>
  </section>
</template>

<script>
import { useRoute } from "vue-router";
import {
  computed,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
  reactive,
  ref,
  watch,
} from "vue";
import IntroductionSection from "./IntroductionSection.vue";
import CurriculumTable from "./CurriculumTable.vue";
import ResourceSection from "./ResourceSection.vue";
import ArticleSection from "./ArticleSection.vue";

export default {
  name: "BookContent",
  components: {
    IntroductionSection,
    CurriculumTable,
    ResourceSection,
    ArticleSection,
  },
  setup() {
    const route = useRoute();
    const activeTab = ref("introduction");
    const lightboxImage = ref("");
    const lightboxAlt = ref("");

    const state = reactive({
      books: {},
      isLoaded: false,
    });

    const { proxy } = getCurrentInstance();
    const currentLang = computed(() => proxy?.$i18n?.locale || "zh");
    const bookPageId = computed(() => route.params.BookPageId || "");
    const iconButtonId = computed(() => route.params.IconButtonId || "");
    const isZh = computed(() => currentLang.value === "zh");

    const getText = (zh, en) => (isZh.value ? zh || en || "" : en || zh || "");

    const resolveImageUrl = (imageUrl) => {
      const value = String(imageUrl || "").trim();
      if (!value) return "";
      if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
        return value;
      }
      return `${process.env.BASE_URL}${value.replace(/^\//, "")}`;
    };

    const splitValues = (value) =>
      String(value || "")
        .split(/[,，\n]/)
        .map((item) => item.trim())
        .filter(Boolean);

    const splitImages = (value) => [
      ...new Set(splitValues(value).map(resolveImageUrl).filter(Boolean)),
    ];

    const formatArrayDataToObject = (dataList) => {
      const headers = dataList[0];
      const temp = {};
      if (!headers) return temp;

      dataList.slice(1).forEach((row) => {
        const item = {};

        headers.forEach((header, columnIndex) => {
          const key = String(header || "").trim();
          if (key) item[key] = row[columnIndex] || "";
        });

        if (!item.id) return;
        if (!temp[item.id]) temp[item.id] = [];
        temp[item.id].push(item);
      });

      return temp;
    };

    const fetchData = async () => {
      state.isLoaded = false;

      try {
        if (!iconButtonId.value) throw new Error("Missing sheet name");

        const sheetID = "15NOAei4LNtVz42k3LV0lz62e4Q4a_K8XZmNPKW3siMI";
        const url = `https://sheets.googleapis.com/v4/spreadsheets/${sheetID}/values/${encodeURIComponent(
          iconButtonId.value,
        )}?alt=json&key=AIzaSyD21a6U3dc4wpee6BCu4D-pxnPff9QSW4w`;

        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Google Sheet request failed: ${response.status}`);
        }

        const data = await response.json();
        state.books = formatArrayDataToObject(data.values || []);
      } catch (error) {
        console.error("BookContent fetch error:", error);
        state.books = {};
      } finally {
        state.isLoaded = true;
      }
    };

    const openLightbox = (image, alt = "") => {
      lightboxImage.value = image;
      lightboxAlt.value = alt;
      document.body.style.overflow = "hidden";
    };

    const closeLightbox = () => {
      lightboxImage.value = "";
      lightboxAlt.value = "";
      document.body.style.overflow = "";
    };

    const handleKeydown = (event) => {
      if (event.key === "Escape" && lightboxImage.value) {
        closeLightbox();
      }
    };

    onMounted(() => {
      fetchData();
      window.addEventListener("keydown", handleKeydown);
    });

    onBeforeUnmount(() => {
      window.removeEventListener("keydown", handleKeydown);
      document.body.style.overflow = "";
    });

    watch(
      () => [route.params.BookPageId, route.params.IconButtonId],
      () => {
        activeTab.value = "introduction";
        fetchData();
      },
    );

    const currentBook = computed(
      () => state.books[bookPageId.value]?.[0] || {},
    );

    const displayArticle = computed(() =>
      getText(
        currentBook.value.article_zh || currentBook.value.article,
        currentBook.value.article_en || currentBook.value.article,
      ),
    );

    // 書籍介紹不跨語言備援：
    // 當前語言沒有 content_zh / content_en 時，改用 img 舊版單圖。
    const displayContent = computed(() => {
      const value = isZh.value
        ? currentBook.value.content_zh || currentBook.value.content || ""
        : currentBook.value.content_en || "";

      return String(value || "").trim();
    });

    const displayProvide = computed(() =>
      getText(
        currentBook.value.provide_zh || currentBook.value.provide,
        currentBook.value.provide_en || currentBook.value.provide,
      ),
    );

    const displayTable = computed(() =>
      getText(
        currentBook.value.table_zh ||
          currentBook.value.tables_zh ||
          currentBook.value.table,
        currentBook.value.table_en ||
          currentBook.value.tables_en ||
          currentBook.value.table,
      ),
    );

    // const tableDefaultTitle = computed(() =>
    //   isZh.value ? "表格介紹" : "Curriculum Overview",
    // );

    const contentImages = computed(() =>
      splitImages(currentBook.value.img || currentBook.value.contentImg),
    );

    const parseFeatureText = (value) => {
      const text = String(value || "")
        .replace(/\r\n?/g, "\n")
        .trim();

      if (!text) {
        return {
          heading: "",
          sections: [],
        };
      }

      const cleanLines = text
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line && !/^(?:-{3,}|={3,})$/.test(line));

      const numberedTitlePattern = /^(\d{1,2})[.、\s_-]+(.+)$/;

      const numberedIndexes = cleanLines
        .map((line, index) => (numberedTitlePattern.test(line) ? index : -1))
        .filter((index) => index >= 0);

      if (numberedIndexes.length) {
        const firstSectionIndex = numberedIndexes[0];
        const heading = cleanLines.slice(0, firstSectionIndex).join(" ").trim();

        const sections = numberedIndexes.map((startIndex, sectionIndex) => {
          const endIndex =
            numberedIndexes[sectionIndex + 1] ?? cleanLines.length;

          const titleLine = cleanLines[startIndex];
          const titleMatch = titleLine.match(numberedTitlePattern);
          const blockLines = cleanLines.slice(startIndex + 1, endIndex);

          const imageLineIndex = blockLines.findIndex((line) =>
            /^(?:圖片|Image)\s*[：:]/i.test(line),
          );

          const image =
            imageLineIndex >= 0
              ? blockLines[imageLineIndex]
                  .replace(/^(?:圖片|Image)\s*[：:]\s*/i, "")
                  .trim()
              : "";

          const contentLines = blockLines.filter(
            (_, index) => index !== imageLineIndex,
          );

          const content = contentLines.join("\n").trim();

          return {
            number: titleMatch?.[1] || String(sectionIndex + 1),
            title: titleMatch?.[2]?.trim() || titleLine,
            image,
            content,
            paragraphs: contentLines.filter(Boolean),
          };
        });

        return {
          heading,
          sections,
        };
      }

      const blocks = text
        .split(/^\s*(?:-{20,}|={6,})\s*$/gm)
        .map((block) => block.trim())
        .filter(Boolean);

      const sections = blocks
        .map((block, index) => {
          const titleMatch = block.match(/【([^】]+)】/);
          const imageMatch = block.match(/(?:圖片|Image)\s*[：:]\s*([^\n]+)/i);

          const title = titleMatch?.[1]?.trim() || "";
          const image = imageMatch?.[1]?.trim() || "";

          const content = block
            .replace(/【[^】]+】/, "")
            .replace(/(?:圖片|Image)\s*[：:]\s*[^\n]+/i, "")
            .trim();

          return {
            number: String(index + 1),
            title,
            image,
            content,
            paragraphs: content
              .split(/\n\s*\n/)
              .map((paragraph) =>
                paragraph
                  .split("\n")
                  .map((line) => line.trim())
                  .filter(Boolean)
                  .join(" "),
              )
              .filter(Boolean),
          };
        })
        .filter((section) => section.title || section.image || section.content);

      return {
        heading: "",
        sections,
      };
    };

    const featureData = computed(() => {
      const zhData = parseFeatureText(currentBook.value.features_zh);
      const enData = parseFeatureText(currentBook.value.features_en);

      const selected = isZh.value ? zhData : enData;
      const fallback = isZh.value ? enData : zhData;

      const source = selected.sections.length ? selected : fallback;
      const other = selected.sections.length ? fallback : selected;

      return {
        heading:
          source.heading ||
          other.heading ||
          (isZh.value ? "本系列三大特色" : "Three Key Features of the Program"),
        sections: source.sections.map((section, index) => ({
          ...section,
          image: resolveImageUrl(
            section.image || other.sections[index]?.image || "",
          ),
        })),
      };
    });

    const featureSections = computed(() => featureData.value.sections);

    const featureSectionTitle = computed(() => featureData.value.heading);

    const provideImages = computed(() =>
      splitImages(currentBook.value.provideImg),
    );

    // Sheet links 支援三種格式：
    // 4 欄：英文標籤|標題|圖片|網址
    //   SAMPLE|The Americas|/images/sample/americas.jpg|https://example.com/sample.pdf
    // 3 欄：英文標籤|標題|網址
    //   SAMPLE|The Americas|https://example.com/sample.pdf
    // 2 欄：標題|網址（保留向下相容）
    //   The Americas|https://example.com/sample.pdf
    const parseLinks = (value) =>
      splitValues(value)
        .map((item, index) => {
          const parts = item.split("|").map((part) => part.trim());
          if (parts.length < 2) return null;

          // 4 欄以上：type | label | image | href
          if (parts.length >= 4) {
            return {
              type: parts[0] || "RESOURCE",
              label: parts[1],
              image: resolveImageUrl(parts[2]),
              href: parts.slice(3).join("|"),
            };
          }

          // 3 欄：type | label | href
          if (parts.length === 3) {
            return {
              type: parts[0] || "RESOURCE",
              label: parts[1],
              image: "",
              href: parts[2],
            };
          }

          // 舊資料維持原本規則：第一筆 DOWNLOAD，其餘 RELATED
          return {
            label: parts[0],
            href: parts[1],
            image: "",
            type: index === 0 ? "DOWNLOAD" : "RELATED",
          };
        })
        .filter((item) => item?.label && item?.href);

    const legacyLinks = computed(() => {
      const result = [];
      const url01 = currentBook.value.url01 || currentBook.value.Url01 || "";
      const url02 = currentBook.value.url02 || currentBook.value.Url02 || "";

      const name01 = getText(
        currentBook.value.urlName01_zh || currentBook.value.UrlName01_zh,
        currentBook.value.urlName01_en || currentBook.value.UrlName01_en,
      );
      const name02 = getText(
        currentBook.value.urlName02_zh || currentBook.value.UrlName02_zh,
        currentBook.value.urlName02_en || currentBook.value.UrlName02_en,
      );

      if (url01 && name01)
        result.push({ href: url01, label: name01, type: "DOWNLOAD" });
      if (url02 && name02)
        result.push({ href: url02, label: name02, type: "RELATED" });
      return result;
    });

    const resourceLinks = computed(() => {
      const compactValue = getText(
        currentBook.value.links_zh || currentBook.value.links,
        currentBook.value.links_en || currentBook.value.links,
      );

      const compactLinks = parseLinks(compactValue);
      return compactLinks.length ? compactLinks : legacyLinks.value;
    });

    const bookImageAlt = computed(() =>
      getText(currentBook.value.title_zh, currentBook.value.title_en),
    );

    const tabAriaLabel = computed(() =>
      isZh.value ? "書籍內容分頁" : "Book content tabs",
    );

    const introEmptyText = computed(() =>
      isZh.value ? "目前尚無書籍介紹。" : "No introduction is available yet.",
    );

    const resourceEmptyText = computed(() =>
      isZh.value
        ? "目前尚無書籍配備資料。"
        : "No material information is available yet.",
    );

    const resourceTitle = computed(() =>
      isZh.value ? "教材資源與試閱" : "Resources & Related Materials",
    );

    return {
      state,
      activeTab,
      displayContent,
      displayArticle,
      displayProvide,
      displayTable,
      // tableDefaultTitle,
      featureSectionTitle,
      contentImages,
      featureSections,
      provideImages,
      resourceLinks,
      bookImageAlt,
      tabAriaLabel,
      introEmptyText,
      resourceEmptyText,
      resourceTitle,
      lightboxImage,
      lightboxAlt,
      openLightbox,
      closeLightbox,
    };
  },
};
</script>

<style scoped>
.book-content {
  width: 100%;
  margin-top: 0;
  padding: 0 clamp(28px, 4vw, 52px) clamp(42px, 5vw, 64px);
  background: transparent;
}

.content-tabs {
  display: flex;
  gap: 34px;
  padding-top: 28px;
  border-bottom: 1px solid #dfe6ec;
}

.tab-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 0 2px 16px;
  border: 0;
  background: transparent;
  color: #75828e;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  transition: color 0.2s ease;
}

.tab-button::after {
  content: "";
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 3px;
  border-radius: 99px 99px 0 0;
  background: #24618d;
  transform: scaleX(0);
  transform-origin: center;
  transition: transform 0.22s ease;
}

.tab-button:hover,
.tab-button.active {
  color: #173f5f;
}

.tab-button.active::after {
  transform: scaleX(1);
}

.tab-index {
  color: #24618d;
  font-size: 12px;
  letter-spacing: 0.08em;
}

.tab-panel {
  min-height: 160px;
}

.introduction-panel {
  padding-top: 60px;
}

.feature-card-section {
  width: min(100%, 1040px);
  margin: 0 auto 72px;
}

.section-heading {
  margin-bottom: 22px;
  text-align: center;
}

.feature-card-eyebrow {
  display: block;
  margin-bottom: 8px;
  color: #24618d;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.section-heading h2 {
  margin: 0;
  color: #19364d;
  font-size: clamp(24px, 2.5vw, 32px);
  font-weight: 750;
}

.feature-card-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 18px;
}

.feature-text-card {
  position: relative;
  min-height: 240px;
  padding: 28px 24px 26px;
  overflow: hidden;
  border: 1px solid #dfe7ed;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 12px 30px rgba(31, 58, 79, 0.07);
}

.feature-text-card::after {
  content: "";
  position: absolute;
  right: -42px;
  bottom: -50px;
  width: 135px;
  height: 135px;
  border-radius: 50%;
  background: #edf5f9;
}

.feature-text-number {
  position: relative;
  z-index: 1;
  display: block;
  margin-bottom: 22px;
  color: #24618d;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.12em;
}

.feature-text-copy {
  position: relative;
  z-index: 1;
}

.feature-text-copy h3 {
  margin: 0 0 12px;
  color: #263f53;
  font-size: 18px;
  font-weight: 760;
  line-height: 1.5;
}

.feature-text-content {
  color: #687684;
  font-size: 14px;
  line-height: 1.75;
}

.feature-text-content p {
  margin: 0 0 10px;
  text-align: left;
}

.feature-text-content p:last-child {
  margin-bottom: 0;
}

.legacy-content-section {
  display: flex;
  width: min(100%, 1040px);
  margin: 82px auto 0;
  flex-direction: column;
  gap: 0;
}

.legacy-content-image-button {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  cursor: zoom-in;
  appearance: none;
}

.legacy-content-image {
  display: block;
  width: 100%;
  height: auto;
}

.legacy-content-image-hint {
  position: absolute;
  right: 12px;
  bottom: 12px;
  padding: 7px 11px;
  border-radius: 999px;
  background: rgba(23, 63, 95, 0.84);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  pointer-events: none;
}

.empty-state {
  padding: 44px 20px;
  color: #7b8792;
  text-align: center;
}

:global(.image-lightbox) {
  position: fixed;
  z-index: 99999;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px;
  background: rgba(0, 0, 0, 0.78);
}

:global(.lightbox-close) {
  position: absolute;
  z-index: 100000;
  top: -18px;
  right: -18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  padding: 0;
  border: 2px solid rgba(23, 79, 125, 0.12);
  border-radius: 50%;
  background: #fff;
  color: #174f7d;
  font-family: Arial, sans-serif;
  font-size: 30px;
  font-weight: 400;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.32);
}

:global(.lightbox-close:hover) {
  background: #f1f6f9;
  transform: scale(1.05);
}

:global(.lightbox-close:focus-visible) {
  outline: 3px solid rgba(255, 255, 255, 0.9);
  outline-offset: 3px;
}

:global(.lightbox-fade-enter-active),
:global(.lightbox-fade-leave-active) {
  transition: opacity 0.2s ease;
}

:global(.lightbox-fade-enter-from),
:global(.lightbox-fade-leave-to) {
  opacity: 0;
}

.loading-state {
  padding: 48px 0;
  color: #7b8792;
  text-align: center;
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.tab-fade-enter-from,
.tab-fade-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

@media (max-width: 900px) and (min-width: 601px) {
  .feature-card-list {
    grid-template-columns: 1fr;
  }

  .feature-text-card {
    min-height: 0;
  }
}

@media (max-width: 600px) {
  .book-content {
    margin-top: 0;
    padding: 0 20px 38px;
  }

  .content-tabs {
    gap: 22px;
    overflow-x: auto;
  }

  .tab-button {
    flex: 0 0 auto;
    padding-bottom: 13px;
    font-size: 15px;
  }

  .introduction-panel {
    padding-top: 34px;
  }

  .feature-card-section {
    margin-bottom: 52px;
  }

  .feature-card-list {
    grid-template-columns: 1fr;
  }

  .feature-text-card {
    min-height: 0;
    padding: 22px 20px;
  }

  .feature-text-number {
    margin-bottom: 12px;
  }

  .feature-text-copy h3 {
    font-size: 17px;
  }

  .legacy-content-section {
    margin-top: 58px;
  }

  .legacy-content-image-hint {
    display: none;
  }
}


:global(.image-lightbox-panel) {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 92vw;
  max-height: 88vh;
}


:global(.image-lightbox-content) {
  display: block;
  width: auto;
  max-width: 92vw;
  max-height: 88vh;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 18px 55px rgba(0, 0, 0, 0.35);
}
</style>
